// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-80 — THE lab table. One mounted unit for every CareRx surface that shows labs.
//
// WHY (owner, 2026-09-07: "i prefer having a singleton component to this labs in all the carerx
// flow"): labs are rendered in five places — the enrollment door's pre-create panel, the Lab
// Tracker, Clinical Review and its detail, and Screening & Hold — and each grew its own table,
// its own analyte labelling and its own idea of what an operator may edit. The consequences were
// not cosmetic:
//   · four different analyte labellings, and the drug record's stored token disagreed with all of
//     them (see lib/analytes — the tracker showed "TB SCREENING", the form matched 'tb_igra')
//   · the bulk bar existed on the tracker ONLY, so a pharmacist working a twenty-analyte panel
//     anywhere else edited one row at a time
//   · a column added for one screen never appeared on the others
//
// This component owns the table, the labelling (through lib/analytes, the one vocabulary) and the
// bulk bar. A host supplies rows and says which columns it is willing to let the operator change;
// it does not restate the layout.
//
// WHAT IT DELIBERATELY DOES NOT DO: decide where a value is written. That split is doctrine, not
// an accident — carerx_labs is the DRAW order (the tracker's record that blood was taken) and
// carerx_lab_results is the pharmacist's record of what the result SAID. The host passes the
// handlers, so the same table serves both without either one quietly writing the other's table.
import { useMemo, useState } from 'react'
import { analyteLabel, normalizeAnalyte } from '../../lib/analytes'
import SearchableSelect from '../common/SearchableSelect'

/** The Result menu shows "Labs Outside Normal Range" on one line even in a narrow column. */
const RESULT_MENU_WIDTH = 240
import { formatUsDate } from '../../api/format'
import { BASE_RESULTS, resultLabel } from '../../lib/labResults'
import DateInput from '../ui/DateInput'

/** One row. `id` is a carerx_labs id where the host has one; the door works pre-row and passes the analyte. */
export interface LabRow {
  id?: string
  analyte: string
  lab_company?: string | null
  draw_type?: string | null
  draw_status?: string | null
  collected_on?: string | null
  results_on?: string | null
  /** pos_neg from carerx_lab_results, when the host reads results. */
  result?: string | null
  /** The measured value the pharmacist read off the report (carerx_lab_results.value_num). */
  value_num?: string | null
  /** A free-text result such as "Within normal limits" (carerx_lab_results.value_text). */
  value_text?: string | null
  /** The pharmacist's note on this analyte. */
  comments?: string | null
  /** Whether the analyte counts as current under the 12-month rule. */
  current?: boolean
  /**
   * G-10: drawn, but the results have not come back. NOT the same as "not collected" — the draw
   * happened, so it must not be re-ordered, but it is not evidence of currency either. The
   * enrollment door has always asked this; it belongs on the shared row so every surface can.
   */
  no_results?: boolean
  locked?: boolean
  note?: string | null
}

/** What the host lets an operator change. Anything omitted renders read-only. */
export interface LabPanelEditable {
  labCompany?: boolean
  drawType?: boolean
  drawStatus?: boolean
  collectedOn?: boolean
  resultsOn?: boolean
  /** Result entry (pos_neg). Only Clinical Review owns this table — see the header note. */
  result?: boolean
  /** The G-10 "drawn, no results yet" flag (the enrollment door's pre-create panel). */
  noResults?: boolean
  /** Numeric + free-text value and the pharmacist's comment (Clinical Review's result entry). */
  value?: boolean
  comments?: boolean
}

export interface LabPanelProps {
  rows: LabRow[]
  editable?: LabPanelEditable
  drawStatusOptions?: string[]
  drawTypeOptions?: string[]
  /** A list for every row, or per row (e.g. Clinical Review keeps normal-range answers off screening labs). */
  resultOptions?: readonly string[] | ((row: LabRow) => readonly string[])
  /** Apply a patch to ONE row. */
  onEdit?: (row: LabRow, patch: Partial<LabRow>) => void | Promise<void>
  /** Apply a patch to MANY rows at once — the point of the bulk bar. */
  onBulk?: (rows: LabRow[], patch: Partial<LabRow>) => void | Promise<void>
  /**
   * Persist EVERY row at once, with no selection step.
   *
   * The bulk bar answers "set these five to the same value"; this answers "I have typed the whole
   * panel, write it" — which is what entering a twenty-analyte result set actually looks like.
   * Without it a pharmacist types every row and then still has to select them all before anything
   * is saved, or click Save twenty times.
   */
  onSaveAll?: (rows: LabRow[]) => void | Promise<void>
  /** Label for that button, when the host wants to be specific about what gets written. */
  saveAllLabel?: string
  /** Rendered per row at the end, for host-specific actions (re-draw, history…). */
  rowActions?: (row: LabRow) => React.ReactNode
  emptyText?: string
  busy?: boolean
}

const DEFAULT_DRAW_STATUS = ['pending', 'ordered', 'collected', 'redraw_needed']
// The shared vocabulary (lib/labResults) — "indet", not the 13-character word the column cannot hold.
const DEFAULT_RESULTS: readonly string[] = [...BASE_RESULTS, 'na']

const T = {
  text: 'var(--color-text, #1a1a1a)', mute: 'var(--color-text-mute, #6b7280)',
  border: 'var(--color-border, #e5e7eb)', surface: 'var(--color-surface, #fff)',
  warnBg: 'var(--color-warning-bg, #fef3c7)', warnFg: 'var(--color-warning-fg, #92400e)',
}
const th: React.CSSProperties = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: T.mute, padding: '6px 8px', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: `1px solid ${T.border}`, fontSize: 12.5, verticalAlign: 'middle' }
const inp: React.CSSProperties = { border: `1px solid ${T.border}`, borderRadius: 6, padding: '4px 7px', fontSize: 12.5, background: T.surface, color: T.text }
/** Stored tokens are snake_case; the option list a human reads is not. */
const optsFrom = (values: readonly string[]) =>
  values.map(v => ({ value: v, label: v.replace(/_/g, ' ') }))
/** Result codes are short (the column is NVARCHAR(12)); their labels are what a person reads. */
const resultOptsFrom = (values: readonly string[]) =>
  values.map(v => ({ value: v, label: resultLabel(v) }))

const btn: React.CSSProperties = { ...inp, cursor: 'pointer', fontWeight: 600 }

// Two different jobs that were one helper, which is how a raw ISO date reached the screen.
// isoForInput feeds <input type="date">, whose value attribute REQUIRES yyyy-mm-dd; formatUsDate
// is what a human reads. Gate 16 caught the display path.
const isoForInput = (v?: string | null) => (v ? String(v).slice(0, 10) : '')
const shownDate = (v?: string | null) => (v ? formatUsDate(String(v)) : '')

export default function LabPanel({
  rows, editable = {}, drawStatusOptions, drawTypeOptions, resultOptions,
  onEdit, onBulk, onSaveAll, saveAllLabel = 'Save all', rowActions,
  emptyText = 'No baseline labs are configured for this drug.', busy,
}: LabPanelProps) {
  const key = (r: LabRow) => r.id ?? normalizeAnalyte(r.analyte)
  const resultsFor = (r: LabRow) =>
    typeof resultOptions === 'function' ? resultOptions(r) : (resultOptions ?? DEFAULT_RESULTS)
  const selectable = rows.filter(r => !r.locked)
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [bulk, setBulk] = useState<Partial<LabRow>>({})
  // The bulk bar writes the SAME result to every ticked row, so it offers only the answers every
  // one of them accepts (no normal-range answer once a screening lab is ticked).
  const bulkTargets = selectable.filter(r => checked.has(key(r)))
  const bulkResults = (bulkTargets.length ? bulkTargets : selectable).map(resultsFor)
    .reduce<readonly string[]>((acc, opts) => acc.filter(o => opts.includes(o)), resultsFor(selectable[0] ?? { analyte: '' }))

  const toggle = (k: string) =>
    setChecked(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n })

  const selectedRows = useMemo(
    () => rows.filter(r => !r.locked && checked.has(key(r))), [rows, checked])

  const canBulk = !!onBulk && Object.keys(editable).length > 0
  const applyBulk = async () => {
    const patch = Object.fromEntries(Object.entries(bulk).filter(([, v]) => v !== '' && v != null))
    if (!onBulk || selectedRows.length === 0 || Object.keys(patch).length === 0) return
    await onBulk(selectedRows, patch as Partial<LabRow>)
    setBulk({}); setChecked(new Set())
  }

  if (rows.length === 0)
    return <div style={{ fontSize: 12.5, color: T.mute, padding: '10px 0' }}>{emptyText}</div>

  return (
    <div>
      {/* SAVE ALL — no selection step. The bulk bar answers "set these five to the same value";
          this answers "I have typed the whole panel, write it", which is what entering a
          twenty-analyte result set actually looks like. Sits above the table because that is
          where a reader looks after finishing the last row. */}
      {onSaveAll && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            style={{ ...btn, background: '#185FA5', color: '#fff', border: 'none',
                     padding: '6px 14px', opacity: busy ? 0.55 : 1 }}
            disabled={!!busy}
            title={`Write every row on this panel (${rows.filter(r => !r.locked).length})`}
            onClick={() => onSaveAll(rows.filter(r => !r.locked))}>
            {busy ? 'Saving…' : `${saveAllLabel} (${rows.filter(r => !r.locked).length})`}
          </button>
        </div>
      )}
      {/* ── bulk bar ────────────────────────────────────────────────────────────────────────
          Was on the Lab Tracker only. A drug can require twenty analytes; editing them one at a
          time is where a half-finished panel comes from. */}
      {canBulk && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end',
                      padding: '8px 10px', marginBottom: 8, border: `1px solid ${T.border}`,
                      borderRadius: 8, background: 'var(--color-surface-alt, #fafafa)' }}>
          <div style={{ fontSize: 11.5, color: T.mute, alignSelf: 'center' }}>
            <b>{selectedRows.length}</b> selected
          </div>
          {editable.drawStatus && (
            <label style={{ fontSize: 11, color: T.mute }}>Draw status<br />
              <SearchableSelect style={inp} value={String(bulk.draw_status ?? '')} ariaLabel="Draw status"
                placeholder="—" options={optsFrom(drawStatusOptions ?? DEFAULT_DRAW_STATUS)}
                onChange={v => setBulk({ ...bulk, draw_status: v })} /></label>
          )}
          {editable.result && (
            <label style={{ fontSize: 11, color: T.mute }}>Result<br />
              <SearchableSelect style={inp} value={String(bulk.result ?? '')} ariaLabel="Result" menuMinWidth={RESULT_MENU_WIDTH}
                placeholder="—" options={resultOptsFrom(bulkResults)}
                onChange={v => setBulk({ ...bulk, result: v })} /></label>
          )}
          {editable.collectedOn && (
            <label style={{ fontSize: 11, color: T.mute }}>Drawn on<br />
              <DateInput label="Drawn on" style={inp} value={isoForInput(bulk.collected_on)}
                onChange={v => setBulk({ ...bulk, collected_on: v ?? '' })} /></label>
          )}
          {editable.resultsOn && (
            <label style={{ fontSize: 11, color: T.mute }}>Results on<br />
              <DateInput label="Results on" style={inp} value={isoForInput(bulk.results_on)}
                onChange={v => setBulk({ ...bulk, results_on: v ?? '' })} /></label>
          )}
          {editable.labCompany && (
            <label style={{ fontSize: 11, color: T.mute }}>Lab company<br />
              <input style={{ ...inp, width: 150 }} value={String(bulk.lab_company ?? '')}
                placeholder="company…" onChange={e => setBulk({ ...bulk, lab_company: e.target.value })} /></label>
          )}
          {editable.noResults && (
            <label style={{ fontSize: 11.5, display: 'flex', gap: 5, alignItems: 'center', alignSelf: 'center' }}>
              <input type="checkbox" checked={!!bulk.no_results}
                onChange={e => setBulk({ ...bulk, no_results: e.target.checked })} /> No results yet</label>
          )}
          <button style={{ ...btn, opacity: selectedRows.length === 0 || busy ? 0.5 : 1 }}
            disabled={selectedRows.length === 0 || !!busy} onClick={applyBulk}>
            Apply to {selectedRows.length || 'selected'}
          </button>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {canBulk && (
                <th style={th}>
                  <input type="checkbox" aria-label="Select all labs"
                    checked={selectable.length > 0 && checked.size === selectable.length}
                    onChange={e => setChecked(e.target.checked ? new Set(selectable.map(key)) : new Set())} />
                </th>
              )}
              <th style={th}>Analyte</th>
              {editable.labCompany && <th style={th}>Lab company</th>}
              {editable.drawType && <th style={th}>Draw type</th>}
              {editable.drawStatus && <th style={th}>Draw status</th>}
              <th style={th} title="The day blood was drawn">Drawn on</th>
              <th style={th} title="Results date — the 12-month currency clock runs from here">Results on</th>
              {editable.noResults && <th style={th} title="Drawn, but results have not come back">No results</th>}
              {editable.value && <><th style={th}>Value</th><th style={th}>Text</th></>}
              <th style={th}>Result</th>
              {editable.comments && <th style={th}>Comments</th>}
              <th style={th}>Status</th>
              {rowActions && <th style={th} />}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const k = key(r)
              const patch = (p: Partial<LabRow>) => onEdit?.(r, p)
              return (
                <tr key={k} style={{ opacity: r.locked ? 0.55 : 1 }}>
                  {canBulk && (
                    <td style={td}>{r.locked ? '🔒'
                      : <input type="checkbox" aria-label={`Select ${r.analyte}`}
                          checked={checked.has(k)} onChange={() => toggle(k)} />}</td>
                  )}
                  {/* ONE labelling, from lib/analytes. Every surface used to invent its own, and
                      the drug record's stored token matched none of them. */}
                  <td style={{ ...td, fontWeight: 600 }}>
                    {analyteLabel(r.analyte)}
                    {r.note && <span style={{ fontSize: 10, color: T.mute }}> {r.note}</span>}
                  </td>
                  {editable.labCompany && (
                    <td style={td}>{r.locked ? (r.lab_company || '—')
                      : <input style={{ ...inp, width: 140 }} defaultValue={r.lab_company ?? ''} placeholder="company…"
                          onBlur={e => e.target.value !== (r.lab_company ?? '') && patch({ lab_company: e.target.value })} />}</td>
                  )}
                  {editable.drawType && (
                    <td style={td}>{r.locked ? (r.draw_type || '—')
                      : <SearchableSelect style={inp} value={r.draw_type ?? ''} ariaLabel="Draw type"
                          placeholder="—" options={optsFrom(drawTypeOptions ?? [])}
                          onChange={v => patch({ draw_type: v })} />}</td>
                  )}
                  {editable.drawStatus && (
                    <td style={td}>{r.locked ? <span>locked · {(r.draw_status ?? '').replace(/_/g, ' ')}</span>
                      : <SearchableSelect style={inp} value={r.draw_status ?? ''} ariaLabel="Draw status"
                          placeholder="—" options={optsFrom(drawStatusOptions ?? DEFAULT_DRAW_STATUS)}
                          onChange={v => patch({ draw_status: v })} />}</td>
                  )}
                  <td style={td}>{r.locked || !editable.collectedOn ? (shownDate(r.collected_on) || '—')
                    : <DateInput label={`Draw date for ${r.analyte}`} style={{ ...inp, width: 138 }} value={isoForInput(r.collected_on)}
                        title="Draw date" onChange={v => patch({ collected_on: v ?? '' })} />}</td>
                  {/* Amber when collected with no results date: that combination reads as "needs
                      labs" at every later stop, and the tracker is where it gets fixed. */}
                  <td style={td}>{r.locked || !editable.resultsOn ? (shownDate(r.results_on) || '—')
                    : <DateInput label={`Results date for ${r.analyte}`} value={isoForInput(r.results_on)}
                        title="Results date — the 12-month currency clock runs from here"
                        style={{ ...inp, width: 138,
                          border: r.draw_status === 'collected' && !r.results_on ? '1px solid #d97706' : inp.border }}
                        onChange={v => patch({ results_on: v ?? '' })} />}</td>
                  {editable.noResults && (
                    <td style={td}>{r.locked ? (r.no_results ? 'yes' : '—')
                      : <input type="checkbox" aria-label={`No results yet for ${r.analyte}`}
                          checked={!!r.no_results}
                          onChange={e => patch({ no_results: e.target.checked })} />}</td>
                  )}
                  {editable.value && (<>
                    <td style={td}><input style={{ ...inp, width: 66 }} defaultValue={r.value_num ?? ''}
                      aria-label={`Value for ${r.analyte}`}
                      onBlur={e => e.target.value !== (r.value_num ?? '') && patch({ value_num: e.target.value })} /></td>
                    <td style={td}><input style={{ ...inp, width: 96 }} defaultValue={r.value_text ?? ''}
                      aria-label={`Result text for ${r.analyte}`}
                      onBlur={e => e.target.value !== (r.value_text ?? '') && patch({ value_text: e.target.value })} /></td>
                  </>)}
                  <td style={td}>{r.locked || !editable.result
                    ? (resultLabel(r.result) || '—')
                    : <SearchableSelect style={inp} value={r.result ?? ''} ariaLabel="Result" menuMinWidth={RESULT_MENU_WIDTH}
                        placeholder="—" options={resultOptsFrom(resultsFor(r))}
                        onChange={v => patch({ result: v })} />}</td>
                  {editable.comments && (
                    // ASP #13: a comment is free text, often a sentence — multi-line like the consultation
                    // note. No maxLength: carerx_lab_results.comments is NVARCHAR(MAX), no server cap.
                    <td style={td}><textarea rows={2} style={{ ...inp, width: 180, minHeight: 34, resize: 'vertical', fontFamily: 'inherit' }}
                      defaultValue={r.comments ?? ''}
                      aria-label={`Comments for ${r.analyte}`}
                      onBlur={e => e.target.value !== (r.comments ?? '') && patch({ comments: e.target.value })} /></td>
                  )}
                  <td style={td}>{r.current === undefined ? '—'
                    : r.current
                      ? <span style={{ color: '#0f5c4a', fontWeight: 700, fontSize: 11 }}>CURRENT</span>
                      : <span style={{ color: '#b91c1c', fontWeight: 700, fontSize: 11 }}>REQUIRED</span>}</td>
                  {rowActions && <td style={td}>{rowActions(r)}</td>}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
